import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
    addAppointment,
    addAttachment,
    addComplaintSparePart,
    addDoc,
    addPosition,
    changeAppointment,
    changeAttachment,
    changeDoc,
    changePosition,
    DocumentManagerService,
    getSelectedComplaint,
    getSynced,
    openDoc,
    removeAppointment,
    removeAttachment,
    splitedDocument,
    removeComplaintSparePart,
    addComplaintNote,
} from '@icc/b2b/sales-document';
import {
    Complaint,
    ComplaintAppointment,
    ComplaintData,
    ComplaintNote,
    ComplaintPosition,
    ComplaintsDocumentType,
    ComplaintSparePart,
    ComplaintUserType,
    Contractor,
    DocumentAttachment,
    DocumentNumberingService,
    DocumentStatus,
    PersonsService,
    Workflow,
    WorkflowData,
} from '@icc/b2b/shared';
import { ConfigService, DynamicElementsService, TranslateService, core, logger } from '@icc/shared';
import { DialogService } from '@icc/ui';
import { Store } from '@ngrx/store';
import { ComplaintParty, ComplaintPartyExtended } from 'libs/b2b/shared/src/lib/models/complaint-party';
import { ComplaintInfoComponent } from './complaint-info/complaint-info.component';
import { ComplaintPositionDialogComponent } from './complaint-position-dialog/complaint-position-dialog.component';
import { BehaviorSubject, Observable, Subscription, firstValueFrom } from 'rxjs';
import { ToastService } from '@icc/shared/helpers';
import { Router } from '@angular/router';
import { ComplaintAppointmentDialogComponent } from './complaint-appointment-dialog/complaint-appointment-dialog.component';
import { ComplaintAppointmentProtocolDialogComponent } from './complaint-appointment-protocol-dialog/complaint-appointment-protocol-dialog.component';
import { ComplaintAppointmentDeleteDialogComponent } from './complaint-appointment-delete-dialog/complaint-appointment-delete-dialog.component';
import { ComplaintSparePartsDialogComponent } from './complaint-spare-parts-dialog/complaint-spare-parts-dialog.component';
import { ComplaintNoteDialogComponent } from './complaint-note-dialog/complaint-note-dialog.component';
import { ComplaintAppointmentProtocolEmailSendComponent } from './complaint-appointment-protocol-email-send/complaint-appointment-protocol-email-send.component';
import { ComplaintEditDialogComponent } from './complaint-edit-dialog/complaint-edit-dialog.component';
import { ComplaintTrigger, ComplaintTriggerParams } from 'libs/b2b/shared/src/lib/models/complaint-workflow-rule';

interface ComplaintCat {
    id: number;
    name: string;
    children: ComplaintCat;
}

@Injectable({
    providedIn: 'root',
})
export class ComplaintService {
    data: ComplaintData = {} as ComplaintData;
    contractorContractors: Contractor[] = [];
    openedDocument$ = this.store.select(getSelectedComplaint) as Observable<Complaint>;
    contractors: Record<number, ComplaintPartyExtended[]> = {};
    allContractors: ComplaintPartyExtended[] = [];
    cachedAttachments: Map<string, any> = new Map();
    private isProcessingPdfSubject = new BehaviorSubject<boolean>(false);
    isProcessingPdf$ = this.isProcessingPdfSubject.asObservable();

    loaded: Promise<boolean>;
    loading = false;
    private resolve: (value: boolean) => void;

    contractorsLoaded: Promise<boolean>;
    contractorsLoading = false;
    private contractorsResolve: (value: boolean) => void;

    workflowsLoaded: Promise<boolean>;
    workflowsLoading = false;
    private workflowsResolve: (value: boolean) => void;

    workflows: Workflow[] = [];
    statuses: DocumentStatus[] = [];

    constructor(
        private http: HttpClient,
        private store: Store,
        private dialogService: DialogService,
        private sanitizer: DomSanitizer,
        private personsService: PersonsService,
        private configService: ConfigService,
        private translateService: TranslateService,
        private dynamicElementsService: DynamicElementsService,
        private toastService: ToastService,
        private router: Router,
        private documentManagerService: DocumentManagerService
    ) {
        this.loaded = new Promise((resolve) => {
            this.resolve = resolve;
        });
        this.contractorsLoaded = new Promise((resolve) => {
            this.contractorsResolve = resolve;
        });
        this.workflowsLoaded = new Promise((resolve) => {
            this.workflowsResolve = resolve;
        });
        this.load();
        this.loadWorkflows();
        this.loadContractors();
    }

    async loadContractors() {
        if (this.configService?.user?.user?.ActivePanel !== 'complaintb2c') {
            if (!this.contractorsLoading) {
                this.contractorsLoading = true;
                await this.loadAllContractors();
                this.contractorsLoading = false;
                this.contractorsResolve(true);
            }
        }
    }

    async loadWorkflows() {
        if (!this.workflowsLoading) {
            this.workflowsLoading = true;
            const data = await firstValueFrom(
                this.http.get<WorkflowData>(
                    `${window.location.origin}/iccadmin/${
                        this.configService?.user?.user?.ActivePanel === 'complaintb2c'
                            ? 'complaintb2c'
                            : 'trade'
                    }/trade/document-statuses-workflows/get-data`
                )
            );
            if (data) {
                this.workflows = data.workflows;
                this.statuses = data.statuses;
            }
            this.workflowsLoading = false;
            this.workflowsResolve(true);
        }
    }

    public isSynced$() {
        return this.store.select(getSynced);
    }

    async load() {
        if (!this.loading) {
            this.loading = true;
            await this.loadData();
            this.resolve(true);
        }
    }

    async loadData() {
        try {
            this.data = (await this.http
                .get(
                    `${window.location.origin}/iccadmin/${
                        this.configService?.user?.user?.ActivePanel === 'complaintb2c'
                            ? 'complaintb2c'
                            : 'trade'
                    }/complaint/complaint-categories/get-data`
                )
                .toPromise()) as ComplaintData;
        } catch (e) {
            console.error(e);
        }
    }

    async saveB2cComplaint(
        complaint: Complaint,
        complaintPositions: ComplaintPosition[],
        complaintPositionsAttachments: DocumentAttachment[]
    ) {
        const positionUuidMap = new Map<string, string>();
        let uuid = '';
        complaint.uuid = 'c-' + core.generateUUID();
        complaintPositions.forEach((position, index) => {
            uuid = position.uuid;
            position.uuid = core.generateUUID();
            position.documentId = complaint.uuid;
            positionUuidMap.set(uuid, position.uuid);
            position.index = index + 1;
            position.complaintCategoryId = this.getComplaintPositionCategoryId(position.configuration);
        });
        complaintPositionsAttachments.forEach((attachment) => {
            attachment.uuid = core.generateUUID();
            attachment.documentId = complaint.uuid;
            attachment.documentPositionId =
                positionUuidMap.get(attachment.documentPositionId ?? '') ?? '';
        });
        complaint.documentNumber = this.generateDocumentNumber(complaint);
        const { positionsWorkflows, positionsWorkflowsGroups } = await this.getPositionsWorkflows(
            complaint,
            complaintPositions
        );
        const positionsGroups = this.groupPositions(positionsWorkflowsGroups);
        complaint.multipleWorkflows = positionsGroups.size > 1;
        complaint.statusId =
            this.statuses.find((el) => el.documentTypeId === complaint.documentTypeId && el.new)
                ?.id ?? 0;
        if (complaint.assignedTo === 'authorizedRetailer') {
            complaint.authorizedRetailerStatusId = this.statuses.find(
                (el) =>
                    el.documentTypeId === complaint.documentTypeId &&
                    el.complaintHandlingLevel === 'authorizedRetailer' &&
                    el.new
            )?.id;
            if (complaint.authorizedRetailerStatusId) {
                complaint.statusHistory = [complaint.authorizedRetailerStatusId];
                complaint.authorizedRetailerStatusAuto = true;
            }
        } else if (complaint.assignedTo === 'distributor') {
            complaint.distributorStatusId = this.statuses.find(
                (el) =>
                    el.documentTypeId === complaint.documentTypeId &&
                    el.complaintHandlingLevel === 'distributor' &&
                    el.new
            )?.id;
            if (complaint.distributorStatusId) {
                complaint.statusHistory = [complaint.distributorStatusId];
                complaint.distributorStatusAuto = true;
            }
        } else if (complaint.assignedTo === 'manufacturer') {
            complaint.manufacturerStatusId = this.statuses.find(
                (el) =>
                    el.documentTypeId === complaint.documentTypeId &&
                    el.complaintHandlingLevel === 'manufacturer' &&
                    el.new
            )?.id;
            if (complaint.manufacturerStatusId) {
                complaint.statusHistory = [complaint.manufacturerStatusId];
                complaint.manufacturerStatusAuto = true;
            }
        }

        complaintPositions.forEach((complaintPosition) => {
            complaintPosition.title = this.getPositionTitle(complaintPosition, true);
        });

        try {

            const formData = new FormData();
            formData.append('complaint', JSON.stringify(complaint));
            formData.append('complaintPositions', JSON.stringify(complaintPositions));
            complaintPositionsAttachments.forEach((attachment) => {
                formData.append(`complaintPositionsAttachment`, core.arrayBufferToBlob(attachment.data, attachment.fileType), attachment.uuid);
            });
            formData.append('complaintPositionsAttachments', JSON.stringify(complaintPositionsAttachments));
            const result = await firstValueFrom(this.http.post<{
                uuid: string;
                documentNumber: string[];
            }>(
                '/api/b2b/complaints/b2c',
                formData,
            ));

            if (result.uuid) {
                complaint.documentNumber = result.documentNumber;
                return true;
            }
            return false;
        } catch (e) {
            logger.error(e);
            return false;
        }
    }

    generateDocumentNumber(complaint: Complaint, newVersion = false) {
        const { key: documentNumber } = DocumentNumberingService.generate(
            this.configService.user.documentNumberingScheme[
                DocumentNumberingService.getNumberingScheme(complaint)
            ],
            {
                buyerId: 0,
                client: {
                    reference: '',
                },
                sellerUserCode: this.configService?.user?.user?.ActiveCompanyCode ?? '',
                marketCode: this.configService.user.market,
                documentCustomCode: '',
                number: newVersion ? complaint.number + '' : '',
                userId: this.configService.user?.user?.id ?? 0,
                creationDate: typeof complaint.created === 'string' ? new Date(complaint.created) : complaint.created,
            }
        );
        return documentNumber;
    }

    deleteB2cComplaint(
        complaint: Complaint,
        complaintPositions: ComplaintPosition[],
        complaintAttachments: DocumentAttachment[]
    ) {
        this.store.dispatch(
            changeDoc({
                id: complaint.uuid,
                changes: {
                    isDeleted: true,
                },
                onlySales: true,
            })
        );
        complaintPositions.forEach((position) => {
            this.store.dispatch(
                changePosition({
                    id: position.uuid,
                    docId: position.documentId,
                    changes: {
                        isDeleted: true,
                    },
                })
            );
        });
        complaintAttachments.forEach((attachment) => {
            this.store.dispatch(
                removeAttachment({
                    id: attachment.uuid,
                    docId: attachment.documentId,
                    isDeleted: true,
                })
            );
        });
    }

    getPositionTitle(position: ComplaintPosition, showFullPositionTitle: boolean): string[] {
        const title = [];
        title.push(position.complaintProductType);
        if (showFullPositionTitle) {
            this.getFullDefectPlace(title, position.configuration);
        } else {
            this.getDefectPlace(title, position.configuration);
        }

        return title;
    }

    openInfo(
        type:
            | 'purchaseDate'
            | 'serialNumber'
            | 'invoiceNumber'
            | 'orderNumber'
            | 'closeUpPhoto'
            | 'wholeProductPhoto'
            | 'description',
        complaintProductTypeId: number
    ) {
        const productType = this.data.complaintProductTypes.find(
            (cpt) => cpt.id === complaintProductTypeId
        );
        if (productType) {
            const info = this.sanitizer.bypassSecurityTrustHtml(
                productType[
                    (type + 'Instruction') as
                        | 'purchaseDateInstruction'
                        | 'serialNumberInstruction'
                        | 'invoiceNumberInstruction'
                        | 'orderNumberInstruction'
                        | 'closeUpPhotoInstruction'
                        | 'wholeProductPhotoInstruction'
                        | 'descriptionInstruction'
                ] ?? ''
            );
            this.dialogService.open(ComplaintInfoComponent, {
                data: {
                    info,
                },
            });
        }
    }

    async openGlobalInfo(
        slug: 'complaint-purchase-date-instruction' | 'complaint-description-instruction'
    ) {
        await this.dynamicElementsService.loaded$.toPromise();
        const info = this.sanitizer.bypassSecurityTrustHtml(
            this.dynamicElementsService.getElementBySlug(slug)?.content ?? ''
        );
        this.dialogService.open(ComplaintInfoComponent, {
            data: {
                info,
            },
        });
    }

    async openDescriptionInfo(complaintProductTypeId: number, complaintCategoryId: number) {
        const productType = this.data.complaintProductTypes.find(
            (cpt) => cpt.id === complaintProductTypeId
        );

        await this.dynamicElementsService.loaded$.toPromise();

        const categories = this.data.complaintCategories[complaintProductTypeId];

        const category = Array.isArray(categories)
            ? this.findCategoryById(categories, complaintCategoryId)
            : null;

        const productTypeDescriptionInfo = productType ? productType['descriptionInstruction'] : '';
        const categoryDescriptionInfo = category ? category['descriptionInstruction'] : '';

        if (productType || category) {
            const info = this.sanitizer.bypassSecurityTrustHtml(
                (this.dynamicElementsService.getElementBySlug('complaint-description-instruction')
                    ?.content ?? '') +
                    (productTypeDescriptionInfo ?? '') +
                    (categoryDescriptionInfo ?? '')
            );
            this.dialogService.open(ComplaintInfoComponent, {
                data: {
                    info,
                },
            });
        }
    }

    findCategoryById(categories: any[], complaintCategoryId: number): any {
        for (const category of categories) {
            if (category.id === complaintCategoryId) {
                return category;
            } else if (category.children && category.children.length > 0) {
                const found = this.findCategoryById(category.children, complaintCategoryId);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }

    categoryHasChildren(complaintProductTypeId: number, complaintCategoryId: number) {
        const categories = this.data.complaintCategories[complaintProductTypeId];

        const category = Array.isArray(categories)
            ? this.findCategoryById(categories, complaintCategoryId)
            : null;

        return category?.children?.length > 0;
    }

    categoryHasNonStatisticalChildren(complaintProductTypeId: number, complaintCategoryId: number) {
        if (this.data.complaintCategories) {
            const categories = this.data.complaintCategories[complaintProductTypeId];

            const category = Array.isArray(categories)
                ? this.findCategoryById(categories, complaintCategoryId)
                : null;

            return category?.children?.some((child) => !child.isStatisticalCategory);
        }
        return true;
    }

    public async getWorkersList(): Promise<any> {
        await this.personsService.loaded;
        return this.personsService.getWorkersList();
    }

    public async getAllContractors(): Promise<any> {
        await this.contractorsLoaded;
        return this.allContractors;
    }

    public openDocument(document: Complaint) {
        this.store.dispatch(openDoc({ id: document.uuid }));
    }

    public openDocumentByUuid(uuid: string) {
        this.store.dispatch(openDoc({ id: uuid }));
    }

    hasAccessToComplaint(document: Complaint) {
        return (
            Number(document?.authorizedRetailerCompanyId) ===
                Number(this.configService?.user?.user?.ActiveCompany) ||
            Number(document?.distributorCompanyId) ===
                Number(this.configService?.user?.user?.ActiveCompany) ||
            document.formerDistributors?.[Number(this.configService?.user?.user?.ActiveCompany)] !=
                null ||
            Number(document?.manufacturerCompanyId) ===
                Number(this.configService?.user?.user?.ActiveCompany)
        );
    }

    getDefectPlace(data: string[], positionConf: any) {
        if (positionConf?.name) {
            data.push(positionConf.name);
        }
        if (positionConf?.children && positionConf?.children.isStatisticalCategory !== true) {
            this.getDefectPlace(data, positionConf.children);
        }
    }

    getFullDefectPlace(data: string[], positionConf: any) {
        if (positionConf?.name) {
            data.push(positionConf.name);
        }
        if (positionConf?.children) {
            this.getFullDefectPlace(data, positionConf.children);
        }
    }

    async changeUser(document: Complaint, user: ComplaintPartyExtended, userType: string) {
        const changes: Partial<Complaint> = {};
        const emptyAuthorizedRetailer = {
            userId: null,
            companyId: null,
            referenceName: '',
            companyName: '',
            email: '',
            phoneNumber: '',
            address: '',
            companyVatNr: '',
            companyVatNrEu: '',
        }
        if (userType === 'authorizedRetailer') {
            changes.authorizedRetailerUserId = user.userId;
            changes.authorizedRetailerUserName = user.referenceName;
            changes.authorizedRetailerCompanyId = user.companyId;
            changes.authorizedRetailerPhoneNumber = user.phoneNumber;
            changes.authorizedRetailerEmail = user.email;
            changes.authorizedRetailer = this.contractorToComplaintParty(user);
        } else if (userType === 'distributor') {
            changes.distributorUserId = user.userId;
            changes.distributorUserName = user.referenceName;
            changes.distributorCompanyId = user.companyId;
            changes.distributorPhoneNumber = user.phoneNumber;
            changes.distributorEmail = user.email;
            changes.distributor = this.contractorToComplaintParty(user);
            if (document.authorizedRetailerUserId !== null) {
                changes.authorizedRetailerUserId = null;
            }
            if (document.authorizedRetailerUserName !== '') {
                changes.authorizedRetailerUserName = '';
            }
            if (document.authorizedRetailerCompanyId !== null) {
                changes.authorizedRetailerCompanyId = null;
            }
            if (document.authorizedRetailerPhoneNumber !== '') {
                changes.authorizedRetailerPhoneNumber = '';
            }
            if (document.authorizedRetailerEmail !== '') {
                changes.authorizedRetailerEmail = '';
            }
            if (document.authorizedRetailer && JSON.stringify(document.authorizedRetailer) !== JSON.stringify(emptyAuthorizedRetailer)) {
                changes.authorizedRetailer = emptyAuthorizedRetailer;
            }
        }
        this.store.dispatch(
            changeDoc({
                id: document.uuid,
                changes,
                onlySales: true,
            })
        );
    }

    getParentCompanyId(userType: string, document: Complaint) {
        switch (userType) {
            case 'authorizedRetailer':
                return document.distributorCompanyId;
            case 'distributor':
                return document.manufacturerCompanyId;
            default:
                return null;
        }
    }

    setComplaintParty(
        document: Complaint,
        userType: 'authorizedRetailer' | 'distributor',
        companyId: number
    ) {
        const parentCompanyId = this.getParentCompanyId(userType, document);
        if (!parentCompanyId) {
            return;
        }
        const party = this.contractors[parentCompanyId]?.find((el) => el.companyId === companyId);
        if (!party || !party.companyId) {
            return;
        }
        if (userType === 'authorizedRetailer') {
            document.authorizedRetailer = party;
            document.authorizedRetailerUserId = party.userId;
            document.authorizedRetailerCompanyId = party.companyId;
            this.store.dispatch(
                changeDoc({
                    id: document.uuid,
                    changes: {
                        authorizedRetailer: party,
                        authorizedRetailerUserId: party.userId,
                        authorizedRetailerCompanyId: party.companyId,
                    },
                    onlySales: true,
                })
            );
        } else if (userType === 'distributor') {
            document.distributor = party;
            document.distributorUserId = party.userId;
            document.distributorCompanyId = party.companyId;
            this.store.dispatch(
                changeDoc({
                    id: document.uuid,
                    changes: {
                        distributor: party,
                        distributorUserId: party.userId,
                        distributorCompanyId: party.companyId,
                    },
                    onlySales: true,
                })
            );
        }
    }

    contractorToComplaintParty(contractor: ComplaintPartyExtended): ComplaintParty {
        return {
            userId: contractor.userId,
            companyId: contractor.companyId,
            referenceName: contractor.referenceName,
            companyName: contractor.companyName,
            email: contractor.email,
            phoneNumber: contractor.phoneNumber,
            address: contractor.address,
            companyVatNr: contractor.companyVatNr,
            companyVatNrEu: contractor.companyVatNrEu,
        };
    }

    getAddress(contractor: any): string {
        return (
            (contractor.contact?.addressLine1 ?? '') +
            (contractor.contact?.addressLine2 ? ' ' + contractor.contact?.addressLine2 : '') +
            (contractor.contact?.postalCode ? ', ' + contractor.contact?.postalCode : '') +
            (contractor.contact?.city ? ' ' + contractor.contact?.city : '')
        );
    }

    async editPosition(
        position: ComplaintPosition,
        complaint: Complaint,
        attachments: DocumentAttachment[]
    ) {
        this.dialogService
            .open(ComplaintPositionDialogComponent, {
                data: {
                    position: core.copy(position),
                    complaint,
                    attachments: core.copy(attachments),
                },
            })
            .afterClosed()
            .subscribe((data) => {
                if (data) {
                    const changes: any = {};
                    this.clearValuesByDocumentType(data.position);
                    const productType = this.data.complaintProductTypes.find(
                        (el) => el.id === data.position.complaintProductTypeId
                    );
                    if (data.position.complaintProductType !== productType?.name) {
                        data.position.complaintProductType = productType?.name;
                    }
                    this.updatePositionConfigNames(
                        data.position.configuration,
                        this.data.complaintCategories[data.position.complaintProductTypeId]
                    );

                    Object.keys(position).forEach((k) => {
                        const key = k as keyof ComplaintPosition;
                        if (key === 'configuration') {
                            if (
                                core.stringJson(position.configuration) !==
                                core.stringJson(data.position.configuration)
                            ) {
                                changes.configuration = data.position.configuration;
                                changes.complaintCategoryId = this.getComplaintPositionCategoryId(data.position.configuration);
                            }
                        } else if (
                            !['title', 'created', 'modified'].includes(key) &&
                            data.position[key] !== position[key]
                        ) {
                            changes[key] = data.position[key];
                        }
                    });

                    Object.keys(data.position).forEach((k) => {
                        const key = k as keyof ComplaintPosition;
                        if (!position[key] && data.position[key]) {
                            changes[key] = data.position[key];
                        }
                    });
                    if (
                        changes?.configuration ||
                        changes?.complaintProductTypeId ||
                        changes?.complaintProductType
                    ) {
                        changes.title = this.getPositionTitle(data.position, true);
                    }
                    if (Object.keys(changes).length) {
                        changes.modified = new Date();
                        this.store.dispatch(
                            changePosition({
                                id: position.uuid,
                                docId: position.documentId,
                                changes,
                            })
                        );
                    }
                    attachments.forEach((attachment) => {
                        if (!data.attachments.find((el) => el.uuid === attachment.uuid)) {
                            this.store.dispatch(
                                removeAttachment({
                                    id: attachment.uuid,
                                    docId: attachment.documentId,
                                    isDeleted: true,
                                })
                            );
                        }
                    });
                    data.attachments.forEach((attachment) => {
                        if (!attachments.find((el) => el.uuid === attachment.uuid)) {
                            this.store.dispatch(
                                addAttachment({
                                    attachment,
                                })
                            );
                        } else if (
                            attachments.find((el) => el.uuid === attachment.uuid)?.description !==
                            attachment.description
                        ) {
                            this.store.dispatch(
                                changeAttachment({
                                    id: attachment.uuid,
                                    docId: attachment.documentId,
                                    changes: {
                                        description: attachment.description,
                                    },
                                    onlySales: true,
                                })
                            );
                        }
                    });
                }
            });
    }

    updatePositionConfigNames(config: ComplaintPosition['configuration'], data: any) {
        if (!config.id) {
            return;
        }
        const complaintCategory = data.find((el) => el.id === Number(config.id));
        if (complaintCategory.name !== config.name) {
            config.name = complaintCategory.name;
        }
        if (config.children?.id) {
            this.updatePositionConfigNames(config.children, complaintCategory.children);
        }
    }

    getComplaintPositionCategoryId(configuration: ComplaintPosition['configuration']) {
        let leafCategoryId = configuration?.id;
        let leaf = configuration;
        while (leaf?.children?.id) {
            leafCategoryId = leaf.children.id;
            leaf = leaf.children;
        }

        return leafCategoryId || 0;
    }

    async createComplaintNote(
        complaint: Complaint,
        complaintNote: ComplaintNote,
        complaintsPositionsMap: Map<string, ComplaintPosition>,
        attachments: DocumentAttachment[],
        isReply = false,
    ) {
        const newComplaintNote: { complaintNote: ComplaintNote, attachments: DocumentAttachment[] } = await this.dialogService
            .open(ComplaintNoteDialogComponent, {
                data: {
                    complaint,
                    complaintNote: core.copy(complaintNote),
                    complaintsPositionsMap,
                    attachments: core.copy(attachments),
                    isReply,
                },
            })
            .afterClosed().toPromise();

        if (newComplaintNote) {
            this.store.dispatch(
                addComplaintNote({
                    complaintNote: newComplaintNote.complaintNote,
                })
            );
            this.store.dispatch(
                changeDoc({
                    id: complaint.uuid,
                    changes: {
                        noteAddedAt: new Date(),
                        noteAddedReminderQueuedAt: null,
                        noteAddedReminderSentAt: null,
                    },
                    onlySales: true,
                })
            );
            newComplaintNote.attachments.forEach((attachment) => {
                if (!attachments.find((el) => el.uuid === attachment.uuid)) {
                    this.store.dispatch(
                        addAttachment({
                            attachment,
                        })
                    );
                }
            });
        }
    }

    async manageComplaintSpareParts(
        complaint: Complaint,
    ) {
        const complaintSpareParts: ComplaintSparePart[] = await this.dialogService
            .open(ComplaintSparePartsDialogComponent, {
                data: {
                    complaint,
                },
            })
            .afterClosed().toPromise();

        if (complaintSpareParts) {
            complaintSpareParts.forEach((complaintSparePart) => {
                this.store.dispatch(
                    addComplaintSparePart({
                        complaintSparePart,
                    })
                );
            });
        }
    }

    deleteComplaintSparePart(complaintSparePart: ComplaintSparePart) {
        this.store.dispatch(
            removeComplaintSparePart({
                id: complaintSparePart.uuid,
                docId: complaintSparePart.documentId,
                complaintSparePart,
                isDeleted: true,
            })
        );
    }

    async editAppointment(
        complaint: Complaint,
        appointment: ComplaintAppointment,
        attachments: DocumentAttachment[],
        editMode = false,
        positionsWorkflowsGroups: Map<number, ComplaintPosition[]>,
    ) {
        this.dialogService
            .open(ComplaintAppointmentDialogComponent, {
                data: {
                    complaint,
                    appointment: core.copy(appointment),
                    attachments: core.copy(attachments),
                    editMode,
                },
            })
            .afterClosed()
            .subscribe((data) => {
                if (data) {
                    if (data.appointment) {
                        if (editMode) {
                            const changes: any = {};

                            Object.keys(appointment).forEach((k) => {
                                const key = k as keyof ComplaintAppointment;

                                if (['date', 'protocolDate'].includes(key)) {
                                    if (
                                        new Date(appointment[key] as Date).getTime() !==
                                        new Date(data.appointment[key] as Date).getTime()
                                    ) {
                                        changes[key] = data.appointment[key];
                                    }
                                } else if (
                                    !['created', 'modified'].includes(key) &&
                                    data.appointment[key] !== appointment[key]
                                ) {
                                    changes[key] = data.appointment[key];
                                }
                            });

                            if (Object.keys(changes).length) {
                                changes.modified = new Date();
                                this.store.dispatch(
                                    changeAppointment({
                                        id: appointment.uuid,
                                        docId: appointment.documentId,
                                        changes,
                                    })
                                );
                            }

                            if ('status' in changes) {
                                this.triggerWorkflow('schedule_appointment', complaint, {
                                    appointmentStatus: changes.status,
                                    level: this.getUserType(),
                                }, positionsWorkflowsGroups);
                            }
                        } else {
                            this.store.dispatch(
                                addAppointment({
                                    appointment: data.appointment,
                                })
                            );
                            this.triggerWorkflow('schedule_appointment', complaint, {
                                appointmentStatus: data.appointment.status,
                                level: this.getUserType(),
                            }, positionsWorkflowsGroups);
                        }
                    }
                    attachments.forEach((attachment) => {
                        if (!data.attachments.find((el) => el.uuid === attachment.uuid)) {
                            this.store.dispatch(
                                removeAttachment({
                                    id: attachment.uuid,
                                    docId: attachment.documentId,
                                    isDeleted: true,
                                })
                            );
                        }
                    });
                    data.attachments.forEach((attachment) => {
                        if (!attachments.find((el) => el.uuid === attachment.uuid)) {
                            this.store.dispatch(
                                addAttachment({
                                    attachment,
                                })
                            );
                        } else if (
                            attachments.find((el) => el.uuid === attachment.uuid)?.description !==
                            attachment.description
                        ) {
                            this.store.dispatch(
                                changeAttachment({
                                    id: attachment.uuid,
                                    docId: attachment.documentId,
                                    changes: {
                                        description: attachment.description,
                                    },
                                    onlySales: true,
                                })
                            );
                        }
                    });
                }
            });
    }

    deleteAppointment(appointment: ComplaintAppointment) {
        this.dialogService
            .open(ComplaintAppointmentDeleteDialogComponent, {
                data: {
                    appointment,
                },
            })
            .afterClosed();
    }

    async confirmSendingProtocol(appointment: ComplaintAppointment) {
        const complaintAppointment = await this.dialogService
            .open(ComplaintAppointmentProtocolEmailSendComponent, {
                data: {
                    appointment,
                }
            })
            .afterClosed().toPromise();

        if (complaintAppointment) {
            this.sendAppointmentProtocolEmail(complaintAppointment);
            this.setIsProcessingPdf(true);
        } else {
            this.setIsProcessingPdf(false);
        }
    }

    async sendAppointmentProtocolEmail(appointment: ComplaintAppointment) {
        try {
            await this.http.get(`${window.location.origin}/api/pdf/send-appointment-protocol-pdf-to-client/${appointment.uuid}`, { responseType: 'blob' }).toPromise();
            this.store.dispatch(
                changeAppointment({
                    id: appointment.uuid,
                    docId: appointment.documentId,
                    changes: {
                        protocolPdfSentToClient: true,
                    }
                })
            );
            this.updateProtocolPdfSentOn(appointment);
        } catch (error) {
            console.error(error);
        }
    }

    async editProtocol(
        complaint: Complaint,
        appointment: ComplaintAppointment,
        attachments: DocumentAttachment[],
        positionsWorkflowsGroups: Map<number, ComplaintPosition[]>,
        editMode = false,
        clientView = false
    ) {
        this.dialogService
            .open(ComplaintAppointmentProtocolDialogComponent, {
                data: {
                    complaint,
                    appointment: core.copy(appointment),
                    attachments: core.copy(attachments),
                    editMode,
                    clientView,
                    positionsWorkflowsGroups,
                    userType: this.getUserType(),
                },
                preventCloseOnClickOutside: true,
            })
            .afterClosed();
    }

    saveProtocolFields(appointment: ComplaintAppointment, attachments: DocumentAttachment[]) {
        this.store.dispatch(
            changeAppointment({
                id: appointment.uuid,
                docId: appointment.documentId,
                changes: {
                    protocolDate: appointment.protocolDate,
                    protocolNotes: appointment.protocolNotes,
                    protocolChangedPartsOrActions: appointment.protocolChangedPartsOrActions,
                    paidService: appointment.paidService,
                    repairCost: appointment.repairCost,
                    workStatus: appointment.workStatus,
                    workToDo: appointment.workToDo,
                    protocolUserNotes: appointment.protocolUserNotes,
                    serviceRating: appointment.serviceRating,
                    orderCompliance: appointment.orderCompliance,
                    wingDamages: appointment.wingDamages,
                    roof: appointment.roof,
                    properDoorLocking: appointment.properDoorLocking,
                    doorAdjustment: appointment.doorAdjustment,
                    doorMeasurement: appointment.doorMeasurement,
                },
            })
        );

        if (attachments && attachments.length) {
            attachments.forEach((attachment) => {
                this.store.dispatch(
                    addAttachment({
                        attachment,
                    })
                );
            });
        }

        if (appointment.servicemanSignatureImage) {
            this.updateAppointmentStatusAfterProtocolSigned(appointment);
        }
    }

    updateProtocolPdfSentOn(appointment: ComplaintAppointment) {
        this.store.dispatch(
            changeAppointment({
                id: appointment.uuid,
                docId: appointment.documentId,
                changes: {
                    protocolPdfSentOn: new Date(),
                },
            })
        );
    }

    saveProtocolSignature(appointment: ComplaintAppointment, type: 'client' | 'serviceman') {
        if (type === 'client') {
            this.store.dispatch(
                changeAppointment({
                    id: appointment.uuid,
                    docId: appointment.documentId,
                    changes: {
                        clientSignatureImage: appointment.clientSignatureImage,
                    },
                })
            );
        } else if (type === 'serviceman') {
            this.store.dispatch(
                changeAppointment({
                    id: appointment.uuid,
                    docId: appointment.documentId,
                    changes: {
                        servicemanSignatureImage: appointment.servicemanSignatureImage,
                    },
                })
            );
        }
    }

    removeProtocolAttachment(attachment: DocumentAttachment) {
        this.store.dispatch(
            removeAttachment({
                id: attachment.uuid,
                docId: attachment.documentId,
                isDeleted: true,
            })
        );
    }

    async removeAppointment(appointment: ComplaintAppointment) {
        this.store.dispatch(
            removeAppointment({
                id: appointment.uuid,
                docId: appointment.documentId,
                isDeleted: true,
            })
        );
    }

    async updateAppointmentStatusAfterProtocolSigned(appointment: ComplaintAppointment) {
        if (appointment.workStatus === 'completed') {
            appointment.status = 'completed';
        } else if (appointment.workStatus === 'inProgress') {
            appointment.status = 'completed-pending';
        } else if (appointment.workStatus === 'rejected') {
            appointment.status = 'rejected';
        }
        this.store.dispatch(
            changeAppointment({
                id: appointment.uuid,
                docId: appointment.documentId,
                changes: {
                    status: appointment.status,
                },
            })
        );
    }

    async editComplaint(
        complaint: Complaint,
        appointments: any[],
        positionsWorkflowsGroups: Map<number, ComplaintPosition[]>,
        positions: ComplaintPosition[],
    ) {
        this.dialogService
            .open(ComplaintEditDialogComponent, {
                data: {
                    complaint: core.copy(complaint),
                    appointments: core.copy(appointments),
                    positionsWorkflowsGroups,
                    positions: core.copy(positions),
                },
            })
            .afterClosed();
    }

    clearValuesByDocumentType(position: ComplaintPosition) {
        if (position.complaintDocumentType === 'serialNumber') {
            position.orderNumber = '';
            position.orderPositionNumber = '';
            position.invoiceNumber = '';
        } else if (position.complaintDocumentType === 'orderNumber') {
            position.serialNumber = '';
            position.invoiceNumber = '';
        } else if (position.complaintDocumentType === 'invoiceNumber') {
            position.serialNumber = '';
            position.orderNumber = '';
            position.orderPositionNumber = '';
        }
    }

    changePurchaseDate(document: Complaint, purchaseDate: Date | null) {
        this.store.dispatch(
            changeDoc({
                id: document.uuid,
                changes: {
                    purchaseDate,
                },
                onlySales: true,
            })
        );
    }

    getUserType() {
        if (this.configService.user?.user?.ActiveCompanyLicenseType === 'license') {
            return 'manufacturer';
        } else if (
            this.configService.user?.user?.ActiveCompanyLicenseType === 'sublicense' &&
            this.configService.user?.parentCompany?.licenseType === 'license'
        ) {
            return 'distributor';
        }

        return 'authorizedRetailer';
    }

    getUserTypeName(type: ComplaintUserType) {
        switch (type) {
            case 'authorizedRetailer':
                return this.translateService.instant('COMPLAINT|APS');
                break;
            case 'distributor':
                return this.translateService.instant('COMPLAINT|Dystrybutor');
                break;
            case 'manufacturer':
                return this.translateService.instant('COMPLAINT|Producent');
                break;
        }
    }

    getPriority(document: Complaint) {
        const userType = this.getUserType();

        return document[
            (userType + 'Priority') as
                | 'authorizedRetailerPriority'
                | 'distributorPriority'
                | 'manufacturerPriority'
        ];
    }

    getUserByType(userType: string, document: Complaint): ComplaintParty | undefined {
        let user: ComplaintParty | undefined;
        switch (userType) {
            case 'authorizedRetailer':
                user = document.authorizedRetailer;
                break;
            case 'distributor':
                user = document.distributor;
                break;
            case 'manufacturer':
                user = document.manufacturer;
                break;
            case 'client':
                user = {
                    userId: null,
                    companyId: null,
                    referenceName: document.clientName,
                    companyName: document.clientName,
                    address: document.installationAddress,
                    email: document.clientEmail,
                    phoneNumber: document.clientPhoneNumber,
                    companyVatNr: '',
                    companyVatNrEu: '',
                };
                break;
        }

        return user;
    }

    canChangeUser(userType: string) {
        if (
            this.configService.user?.user?.ActiveCompanyLicenseType === 'license' &&
            (userType === 'distributor' || userType === 'authorizedRetailer')
        ) {
            return true;
        } else if (
            this.configService.user?.user?.ActiveCompanyLicenseType === 'sublicense' &&
            this.configService.user?.parentCompany?.licenseType === 'license' &&
            userType === 'authorizedRetailer'
        ) {
            return true;
        }

        return false;
    }

    changePriority(document: Complaint, priority: string) {
        if (priority.match(/^[1-4][0-9]{2}$/)) {
            const userType = this.getUserType();

            if (
                priority !==
                document[
                    (userType + 'Priority') as
                    | 'authorizedRetailerPriority'
                    | 'distributorPriority'
                    | 'manufacturerPriority'
                ]
            ) {
                document[(userType + 'Priority') as 'authorizedRetailerPriority' | 'distributorPriority' | 'manufacturerPriority'] = priority;
                this.store.dispatch(
                    changeDoc({
                        id: document.uuid,
                        changes: {
                            [(userType + 'Priority') as
                                | 'authorizedRetailerPriority'
                                | 'distributorPriority'
                                | 'manufacturerPriority']: priority,
                        },
                        onlySales: true,
                    })
                );
            }
        }
    }

    changeDocumentName(document: Complaint, name: string) {
        this.store.dispatch(
            changeDoc({
                id: document.uuid,
                changes: {
                    documentName: name,
                },
                onlySales: true,
            })
        );
    }

    changeComplaintType(document: Complaint, complaintType: ComplaintsDocumentType) {
        this.store.dispatch(
            changeDoc({
                id: document.uuid,
                changes: {
                    documentTypeId: +complaintType,
                },
                onlySales: true,
            })
        );
    }

    changeDefectMaintenance(
        document: Complaint,
        position: ComplaintPosition,
        defectMaintenance: number
    ) {
        this.store.dispatch(
            changePosition({
                id: position.uuid,
                docId: position.documentId,
                changes: {
                    defectMaintenance,
                },
            })
        );
    }

    changeStatus(document: Complaint, statusId: number, userType: ComplaintUserType, positionsWorkflowsGroups: Map<number, ComplaintPosition[]> | null = null, auto = false) {
        const changes: Partial<Complaint> = {};
        if (userType === 'authorizedRetailer') {
            changes.authorizedRetailerStatusId = statusId;
            changes.authorizedRetailerStatusAuto = auto;
        } else if (userType === 'distributor') {
            changes.distributorStatusId = statusId;
            changes.distributorStatusAuto = auto;
        } else if (userType === 'manufacturer') {
            changes.manufacturerStatusId = statusId;
            changes.manufacturerStatusAuto = auto;
        }
        changes.statusChangedAt = new Date();
        changes.statusChangeReminderQueuedAt = null;
        changes.statusChangeReminderSentAt = null;
        changes.statusId = statusId;
        changes.statusHistory = [...(document.statusHistory || []), statusId];
        Object.assign(document, changes);

        this.store.dispatch(
            changeDoc({
                id: document.uuid,
                changes,
                onlySales: true,
            })
        );

        if (positionsWorkflowsGroups) {
            this.triggerWorkflow('status_change', document, {
                documentStatus: statusId,
                level: userType,
            }, positionsWorkflowsGroups);
        }
    }

    changeComplaintOrderNumber(document: Complaint, complaintOrderNumber: string) {
        this.store.dispatch(
            changeDoc({
                id: document.uuid,
                changes: {
                    complaintOrderNumber,
                },
                onlySales: true,
            })
        );
    }

    getAssignedTo(document: Complaint, assignedTo: Complaint['assignedTo'], companyId?: number) {
        if (!assignedTo || assignedTo === 'none') {
            return this.translateService.instant('INTERFACE|Brak');
        }
        if (assignedTo !== 'distributor' || +companyId === +document.distributorCompanyId) {
            return (
                this.getAssignedToName(assignedTo) +
                ', ' +
                (document?.[assignedTo]?.companyName ?? '')
            );
        } else if (document.manufacturerCompanyId) {
            const party = this.contractors[document.manufacturerCompanyId]?.find(
                (el) => el.companyId === companyId
            );
            return this.getAssignedToName(assignedTo) + ', ' + (party?.companyName ?? '');
        }
    }

    getAssignedToUserName(
        document: Complaint,
        assignedTo: Complaint['assignedTo']
    ): string {
        if (!assignedTo || assignedTo === 'none') {
            return this.translateService.instant('INTERFACE|Brak');
        }
        return document?.[assignedTo + 'UserName' as 'authorizedRetailerUserName' | 'distributorUserName' | 'manufacturerUserName'] ?? '';
    }

    getAssignedToName(assignedTo: Complaint['assignedTo']) {
        switch (assignedTo) {
            case 'authorizedRetailer':
                return this.translateService.instant('COMPLAINT|APS');
                break;
            case 'distributor':
                return this.translateService.instant('COMPLAINT|Dystrybutor');
                break;
            case 'manufacturer':
                return this.translateService.instant('COMPLAINT|Producent');
                break;
            default:
                return this.translateService.instant('INTERFACE|Brak');
                break;
        }
    }

    getAssignToOptions(document: Complaint) {
        const userType = this.getUserType();
        let assignToOptions: { [key: string]: string } = {};
        if (['authorizedRetailer', 'distributor', 'manufacturer'].includes(userType)) {
            if (document?.authorizedRetailerCompanyId) {
                assignToOptions['authorizedRetailer'] = this.getAssignedTo(
                    document,
                    'authorizedRetailer'
                );
            }
            assignToOptions['distributor-' + document.distributorCompanyId] = this.getAssignedTo(
                document,
                'distributor',
                document.distributorCompanyId
            );

            for (const [distributorId, userId] of Object.entries(
                document.formerDistributors ?? {}
            )) {
                assignToOptions['distributor-' + distributorId] = this.getAssignedTo(
                    document,
                    'distributor',
                    +distributorId
                );
            }
        }
        if (['distributor', 'manufacturer'].includes(userType)) {
            assignToOptions['manufacturer'] = this.getAssignedTo(document, 'manufacturer');
        }

        return assignToOptions;
    }

    changeAssignedTo(
        document: Complaint,
        assignedTo: Complaint['assignedTo'] | string,
        companyId?: number
    ) {
        if (assignedTo?.includes('distributor-')) {
            companyId = +assignedTo.split('-')[1];
            assignedTo = 'distributor';
        }
        if (!companyId) {
            companyId =
                document[
                    (assignedTo + 'CompanyId') as
                        | 'authorizedRetailerCompanyId'
                        | 'distributorCompanyId'
                        | 'manufacturerCompanyId'
                ] ?? 0;
        }
        document.assignedCompanyId = companyId;
        document.assignedTo = assignedTo as Complaint['assignedTo'];
        this.store.dispatch(
            changeDoc({
                id: document.uuid,
                changes: {
                    assignedTo: assignedTo as Complaint['assignedTo'],
                    assignedCompanyId: companyId,
                    assignChangedAt: new Date(),
                    statusChangedAt: new Date(),
                    assignChangeReminderQueuedAt: null,
                    assignChangeReminderSentAt: null,
                    statusChangeReminderQueuedAt: null,
                    statusChangeReminderSentAt: null,
                },
                onlySales: true,
            })
        );
    }

    changeResponsibleUser(document: Complaint, responsibleUserId: number | null) {
        const worker = this.personsService.getWorkerByUserId(responsibleUserId);
        const changes: Partial<Complaint> = {
            [this.getUserType()]: {
                ...document[this.getUserType()],
                userId: responsibleUserId,
                referenceName: worker?.person?.reference_name ?? '',
                email: worker?.person?.email ?? worker?.person?.user?.email ?? '',
                phoneNumber: worker?.person?.phone_number ?? '',
            },
        };
        this.store.dispatch(
            changeDoc({
                id: document.uuid,
                changes,
                onlySales: true,
            })
        );
    }

    changeAssignedUser(document: Complaint, assignedUserId: number) {
        const worker = this.personsService.getWorkerByUserId(assignedUserId);
        let changes: any = {
            [this.getUserType() + 'UserId']: assignedUserId,
            [this.getUserType() + 'UserName']: worker?.person?.reference_name ?? '',
            [this.getUserType() + 'PhoneNumber']: worker?.person?.phone_number ?? '',
            [this.getUserType() + 'Email']: worker?.person?.email ?? worker?.person?.user?.email ?? '',
        };
        if (this.getUserType() === document.assignedTo) {
            changes.assignChangedAt = new Date();
            changes.assignChangeReminderQueuedAt = null;
            changes.assignChangeReminderSentAt = null;
        }
        this.store.dispatch(
            changeDoc({
                id: document.uuid,
                changes,
                onlySales: true,
            })
        );
    }

    async loadAllContractors() {
        const parties = await firstValueFrom(
            this.http.get<ComplaintPartyExtended[]>(
                `${window.location.origin}/api/b2b/complaints/contractors/all`
            )
        );

        if (parties?.length) {
            this.allContractors = parties;
            this.contractors = {};
            parties.forEach((party) => {
                if (!this.contractors[party.ownerCompanyId ?? party.companyId]) {
                    this.contractors[party.ownerCompanyId ?? party.companyId] = [];
                }
                this.contractors[party.ownerCompanyId ?? party.companyId].push(party);
            });
        }
    }

    async validateSerialNumber(serialNumber: string, complaintUuid?: string) {
        return await firstValueFrom(
            this.http.post<{
                valid: boolean;
                aps: string;
                orderId: string;
                positionNo: string;
                authorizedRetailerCompanyId?: number;
            }>(`${window.location.origin}/api/b2b/complaints/validate-serial-number`, {
                serialNumber,
                complaintUuid,
            })
        );
    }

    async cacheAttachment(uuid: string, blob: Blob) {
        this.cachedAttachments.set(
            uuid,
            this.sanitizer.bypassSecurityTrustUrl((await core.blobToBase64(blob)) as string)
        );
    }

    async getPositionsWorkflows(document: Complaint, positions: ComplaintPosition[]) {
        await this.workflowsLoaded;
        const positionsWorkflows = new Map<string, Workflow | null>();
        const positionsWorkflowsGroups = new Map<number, ComplaintPosition[]>();
        positions.forEach((position) => {
            const positionWorkflow = this.getPositionWorkflow(document, position.configuration);
            positionsWorkflows.set(position.uuid, positionWorkflow);
            if (!positionsWorkflowsGroups.has(positionWorkflow?.id ?? 0)) {
                positionsWorkflowsGroups.set(positionWorkflow?.id ?? 0, []);
            }
            positionsWorkflowsGroups.get(positionWorkflow?.id ?? 0)?.push(position);
        });
        return { positionsWorkflows, positionsWorkflowsGroups };
    }

    getPositionWorkflow(
        document: Complaint,
        conf: ComplaintPosition['configuration'],
        workflow: Workflow | null = null
    ): Workflow | null {
        let complaintCreator = 'client';
        if (document.clientName?.length > 0) {
            complaintCreator = 'client';
        } else if (document.authorizedRetailerCompanyId != null) {
            complaintCreator = 'authorized_retailer';
        } else if (document.distributorCompanyId != null) {
            complaintCreator = 'distributor';
        }

        const matchingWorkflow = this.workflows.find(w =>
            w.complaintCategories.includes(conf.id) &&
                w.documentTypeId === +document.documentTypeId &&
                w.complaintCreator === complaintCreator
        );
        if (matchingWorkflow) {
            workflow = core.copy(matchingWorkflow);
        }
        if (conf.children?.id) {
            return this.getPositionWorkflow(document, conf.children, workflow);
        } else {
            return workflow;
        }
    }

    acceptChanges(document: Complaint, positionsWorkflowsGroups: Map<number, ComplaintPosition[]>) {
        const positionsGroups = this.groupPositions(positionsWorkflowsGroups);
        this.toastService.add({
            title: this.translateService.instant('COMPLAINT|Zatwierdzanie podziału'),
            body: this.translateService.instant(
                'COMPLAINT|Potwierdzenie zmian skutkuje <b>nieodwracalnym</b> podziałem zgłoszenia reklamacyjnego.<br>Zgłoszenie zostanie podzielone na {number} nowe zgłoszenia:<br>{positionsGroupsDescription}<br> Czy na pewno chcesz kontynuować?',
                {
                    number: positionsGroups.size,
                    positionsGroupsDescription: this.getPositionsGroupsDescription(positionsGroups),
                }
            ),
            severity: 'question',
            sticky: true,
            buttons: [
                {
                    title: this.translateService.instant('INTERFACE|Zatwierdź'),
                    onClick: () => this.splitDocument(document, positionsWorkflowsGroups),
                },
                {
                    title: this.translateService.instant('INTERFACE|Anuluj'),
                    onClick: () => null,
                },
            ],
        });
    }

    getPositionsGroupsDescription(positionsGroups: Map<string, ComplaintPosition[]>) {
        let description = '';
        let i = 1;
        positionsGroups.forEach((positions) => {
            description += this.translateService.instant('COMPLAINT|Zgłoszenie') + ' ' + i + ': ';
            positions.forEach((position) => {
                description += position.title?.join('>') + ', ';
            });
            description += '<br>';
            i++;
        });

        return description;
    }

    async splitDocument(
        document: Complaint,
        positionsWorkflowsGroups: Map<number, ComplaintPosition[]>
    ) {
        let i = 0;
        const groupedPositions = this.groupPositions(positionsWorkflowsGroups);
        const documentsUuids = [document.uuid];
        for (const [id, positions] of groupedPositions) {
            if (i === 0) {
                i++;
                continue;
            }
            documentsUuids.push(await this.createNewDocumentWithPositions(document, positions, true));
        }
        this.store.dispatch(
            splitedDocument({
                id: document.uuid,
                docId: document.uuid,
                changes: {
                    documentsUuids,
                },
            })
        );
        this.toastService.add({
            title: this.translateService.instant('INTERFACE|Sukces'),
            body: this.translateService.instant(
                'COMPLAINT|Zgłoszenie zostało podzielone na nowe zgłoszenia.'
            ),
            severity: 'success',
        });
    }

    async createNewDocumentWithPositions(document: Complaint, positions: ComplaintPosition[], newVersion: boolean) {
        const newDocument = core.copy(document);
        delete newDocument.id;
        newDocument.uuid = 'c-' + core.generateUUID();
        newDocument.documentNumber = this.generateDocumentNumber(newDocument, newVersion);
        newDocument.created = new Date();
        newDocument.modified = new Date();
        newDocument.multipleWorkflows = false;
        newDocument.parentId = document.uuid;
        newDocument.rootId = document.rootId ?? document.uuid;
        newDocument.rootCreated = document.rootCreated ?? document.created;
        const data = {
            newDocument,
            positions,
        };

        positions.forEach((position) => {
            this.store.dispatch(
                changePosition({
                    id: position.uuid,
                    docId: position.documentId,
                    changes: {
                        movedToDocumentId: newDocument.uuid,
                        isDeleted: true,
                    },
                })
            );
        });

        await this.http
            .post(`${window.location.origin}/api/b2b/copy-positions-to-another-document`, data)
            .toPromise();

        return newDocument.uuid;
    }

    getMaxVersion(documents: Complaint[]) {
        const maxVersion = Math.max(...documents.map((doc) => Number(doc.version) ?? 0));
        const count = documents.length;
        return Math.max(maxVersion, count) + 1;
    }

    groupPositions(positionsWorkflowsGroups: Map<number, ComplaintPosition[]>) {
        const groupedPositions = new Map<string, ComplaintPosition[]>();
        const groups: {
            workflowId: number;
            orderNumber: string;
            invoiceNumber: string;
            positions: ComplaintPosition[];
        }[] = [];
        for (const [workflowId, positions] of positionsWorkflowsGroups) {
            positions.forEach((position) => {
                const groupIndex = groups.findIndex(
                    (group) =>
                        group.workflowId === workflowId &&
                        (!position.orderNumber || !group.orderNumber || group.orderNumber === position.orderNumber) &&
                        (!position.invoiceNumber || !group.invoiceNumber || group.invoiceNumber === position.invoiceNumber)
                );
                if (groupIndex === -1) {
                    groups.push({
                        workflowId,
                        orderNumber: position.orderNumber ?? '',
                        invoiceNumber: position.invoiceNumber ?? '',
                        positions: [position],
                    });
                } else {
                    if (!groups[groupIndex].orderNumber) {
                        groups[groupIndex].orderNumber = position.orderNumber ?? '';
                    }
                    if (!groups[groupIndex].invoiceNumber) {
                        groups[groupIndex].invoiceNumber = position.invoiceNumber ?? '';
                    }
                    groups[groupIndex].positions.push(position);
                }
            });
        }
        groups.forEach((group) => {
            const key = `${group.workflowId}*${group.orderNumber ?? 'null'}*${group.invoiceNumber ?? 'null'}`;
            groupedPositions.set(key, group.positions);
        });
        return groupedPositions;
    }

    changeMultipleWorkflows(document: Complaint, multipleWorkflows: boolean) {
        this.store.dispatch(
            changeDoc({
                id: document.uuid,
                changes: {
                    multipleWorkflows,
                },
                onlySales: true,
            })
        );
    }

    changePositionVerification(position: ComplaintPosition, toVerify: boolean) {
        this.store.dispatch(
            changePosition({
                id: position.uuid,
                docId: position.documentId,
                changes: {
                    toVerify,
                },
            })
        );
    }

    // eslint-disable-next-line max-statements
    async movePositionsToNewDocument(document: Complaint, allPositions: ComplaintPosition[], selectedPositions: string[]) {
        const positions = allPositions.filter((p) => selectedPositions.includes(p.uuid));
        const leftPositions = allPositions.filter((p) => !selectedPositions.includes(p.uuid));
        let { positionsWorkflows, positionsWorkflowsGroups } = await this.getPositionsWorkflows(
            document,
            leftPositions
        );
        let positionsGroups = this.groupPositions(positionsWorkflowsGroups);
        let multipleWorkflows = positionsGroups.size > 1;
        if (document.multipleWorkflows !== multipleWorkflows) {
            this.changeMultipleWorkflows(document, multipleWorkflows);
        }
        ({ positionsWorkflows, positionsWorkflowsGroups } = await this.getPositionsWorkflows(
            document,
            positions
        ));
        positionsGroups = this.groupPositions(positionsWorkflowsGroups);
        multipleWorkflows = positionsGroups.size > 1;
        const newDocument = core.copy(document);
        delete newDocument.id;
        newDocument.uuid = 'c-' + core.generateUUID();
        newDocument.documentNumber = this.generateDocumentNumber(newDocument, true);
        newDocument.created = new Date();
        newDocument.modified = new Date();
        newDocument.multipleWorkflows = false;
        newDocument.parentId = document.uuid;
        newDocument.rootId = document.rootId ?? document.uuid;
        newDocument.rootCreated = document.rootCreated ?? document.created;
        newDocument.multipleWorkflows = multipleWorkflows;
        const data = {
            newDocument,
            positions,
        };

        positions.forEach((position) => {
            this.store.dispatch(
                changePosition({
                    id: position.uuid,
                    docId: position.documentId,
                    changes: {
                        movedToDocumentId: newDocument.uuid,
                        isDeleted: true,
                    },
                })
            );
        });

        await this.http
            .post(`${window.location.origin}/api/b2b/copy-positions-to-another-document`, data)
            .toPromise();

        this.toastService.add({
            title: this.translateService.instant('INTERFACE|Sukces'),
            body: this.translateService.instant(
                'COMPLAINT|Pozycje do weryfikacji zostały przeniesione do nowego zgłoszenia.'
            ),
            severity: 'success',
        });
    }

    getAvailableStatuses(
        document: Complaint,
        positions: ComplaintPosition[],
        positionsWorkflowsGroups: Map<number, ComplaintPosition[]>,
        appointments,
        userType: ComplaintUserType
    ) {
        const documentStatusId = this.getDocumentStatusForUser(document, userType) || -1;
        if (positionsWorkflowsGroups.size > 1) {
            return this.statuses.filter((status) => status.id === +documentStatusId);
        } else {
            const positionsWorkflow = this.workflows.find(
                (el) => +el.id === +positionsWorkflowsGroups.keys().next().value
            );
            const allPositionsHaveConfiguration = this.allPositionsHaveConfiguration(positions);
            return this.statuses.filter(
                (status) =>
                    status.id === +documentStatusId ||
                    (+status.documentTypeId === +document.documentTypeId &&
                        status.complaintHandlingLevel === userType &&
                        positionsWorkflow?.userRolesStatuses.some((el) =>
                            el.level === userType &&
                            el.userRolesIds.some(
                                (roleId) =>
                                (Number(roleId) === 0 ||
                                    this.configService.user.user?.Roles.map(Number).includes(
                                        Number(roleId)
                                    ))
                            ) &&
                            el.statuses[+documentStatusId]?.[status.id]?.available &&
                            this.checkAppointments(
                                appointments,
                                el.statuses[+documentStatusId]?.[status.id]
                            )
                            && (allPositionsHaveConfiguration || !el.statuses.lockedWhenLackConfiguration?.[status.id]?.available)
                        ))
            );
        }
    }

    getDocumentStatusForUser(document: Complaint, userType: ComplaintUserType) {
        return +document[`${userType}StatusId` as 'authorizedRetailerStatusId' | 'distributorStatusId' | 'manufacturerStatusId']!;
    }

    checkAppointments(appointments: ComplaintAppointment[], status) {
        return (
            !status.protocol ||
            appointments.some(
                (el) =>
                    !el.isDeleted &&
                    el.status === 'completed' &&
                    el.protocolDate &&
                    el.servicemanSignatureImage
            )
        );
    }

    getFilteredAssignToOptions(
        assignedToOptions: {
            [key: string]: string;
        },
        positionsWorkflowsGroups: Map<number, ComplaintPosition[]>,
        document: Complaint
    ) {
        if (Object.keys(assignedToOptions).length === 1) {
            return assignedToOptions;
        } else if (positionsWorkflowsGroups.size > 1) {
            return Object.keys(assignedToOptions)
                .filter((key) => document.assignedTo === key)
                .reduce((obj, key) => {
                    obj[key] = assignedToOptions[key];
                    return obj;
                }, {});
        } else {
            const positionsWorkflow = this.workflows.find(
                (el) => +el.id === +positionsWorkflowsGroups.keys().next().value
            );
            return Object.keys(assignedToOptions)
                .filter((key) => {
                    if (key.includes('distributor-')) {
                        key = 'distributor';
                    }

                    return (
                        document.assignedTo === key ||
                        positionsWorkflow?.assignPermissions.some(
                            (assignPermission) =>
                                assignPermission.userRolesIds.some(
                                    (el) =>
                                        Number(el) === 0 ||
                                        this.configService.user.user?.Roles.map(Number).includes(
                                            Number(el)
                                        )
                                ) &&
                                assignPermission.permissions[document.statusId]?.userTypes.includes(
                                    key as ComplaintUserType
                                )
                        )
                    );
                })
                .reduce((obj, key) => {
                    obj[key] = assignedToOptions[key];
                    return obj;
                }, {});
        }
    }

    getStatus(statusId: number) {
        return this.statuses.find((el) => el.id === statusId);
    }

    async getStatuses() {
        await this.workflowsLoaded;
        return this.statuses.filter((el) => el.documentTypeId === ComplaintsDocumentType.Complaint || el.documentTypeId === ComplaintsDocumentType.Service);
    }

    getDeepestNestedCategoryId(node: ComplaintCat): number {
        let maxDepth = -1;
        let deepestId = node.id;

        const traverse = (currentNode: ComplaintCat, currentDepth: number) => {
            if (!currentNode.children || Object.keys(currentNode.children).length === 0) {
                if (currentDepth > maxDepth) {
                    maxDepth = currentDepth;
                    deepestId = currentNode.id;
                }
            } else {
                traverse(currentNode.children, currentDepth + 1);
            }
        };

        traverse(node, 0);
        return deepestId;
    }

    setIsProcessingPdf(value: boolean) {
        this.isProcessingPdfSubject.next(value);
    }

    getTranslatedProductType(complaintProductTypeId: number): string {
        if (!this.data || !this.data.complaintProductTypes) {
            return '';
        }
        return this.data.complaintProductTypes?.find((el) => el.id === complaintProductTypeId)?.name ?? '';
    }

    triggerWorkflow(trigger: ComplaintTrigger, document: Complaint, params: ComplaintTriggerParams, positionsWorkflowsGroups: Map<number, ComplaintPosition[]>) {
        const positionsWorkflow = this.workflows.find(
            (el) => +el.id === +positionsWorkflowsGroups.keys().next().value
        );

        if (!positionsWorkflow) {
            return;
        }

        const rules = positionsWorkflow?.rules.filter((el) => {
            if (el.trigger !== trigger) {
                return false;
            }
            if (trigger === 'button_click') {
                return el.triggerParams.button === params.button;
            }
            if (trigger === 'protocol_confirmed') {
                return el.triggerParams.protocolStatus === params.protocolStatus && el.triggerParams.level === params.level;
            }
            if (trigger === 'status_change') {
                return +el.triggerParams.documentStatus === +params.documentStatus;
            }
            if (trigger === 'schedule_appointment') {
                return el.triggerParams.appointmentStatus === params.appointmentStatus && el.triggerParams.level === params.level;
            }
            return true;
        }).filter(rule => {
            let isValid = true;
            for (const condition of rule.conditions) {
                const statusSetAuto = document[
                    (condition.params.level + 'StatusAuto') as 'authorizedRetailerStatusAuto' | 'distributorStatusAuto' | 'manufacturerStatusAuto'
                ];
                const statusId = document[
                    (condition.params.level + 'StatusId') as 'authorizedRetailerStatusId' | 'distributorStatusId' | 'manufacturerStatusId'
                ] || 0;
                if (condition.condition === 'status_set_auto' && !statusSetAuto) {
                    isValid = false;
                }
                if (condition.condition === 'status_set_manually' && statusSetAuto) {
                    isValid = false;
                }
                if (condition.condition === 'historical_status' && !(document.statusHistory || []).map(Number).includes(+condition.params.documentStatus!)) {
                    isValid = false;
                }
                if (condition.condition === 'not_historical_status' && (document.statusHistory || []).map(Number).includes(+condition.params.documentStatus!)) {
                    isValid = false;
                }
                if (condition.condition === 'current_status' && +statusId !== +condition.params.documentStatus!) {
                    isValid = false;
                }
            }
            return isValid;
        });

        if (!rules.length) {
            return;
        }

        for (const rule of rules) {
            logger.log('triggerWorkflow', trigger, rule, document.uuid, params, 'id procesu:', positionsWorkflow.id);

            for (const action of rule.actions) {
                switch (action.action) {
                    case 'set_status':
                        if (action.params.documentStatus && action.params.level) {
                            this.changeStatus(document, +action.params.documentStatus, action.params.level, positionsWorkflowsGroups, true);
                        }
                        break;
                    case 'assign_subject':
                        this.changeAssignedTo(document, action.params.subject as ComplaintUserType);
                        break;
                }
            }
        }
    }

    allPositionsHaveConfiguration(positions: ComplaintPosition[]): boolean {
        if (this.configService.user?.user?.ActiveCompanyLicenseType === 'license') {
            return true;
        }
        return positions.every(position => position.configuration && Object.keys(position.configuration).length > 0 && !this.categoryHasNonStatisticalChildren(position.complaintProductTypeId, +this.getDeepestNestedCategoryId(position.configuration)));
    }
}
